/**
 * Theme context for managing dark/light mode across the application
 * Provides theme switching functionality and persists user preference
 */

import { createContext, useContext, useEffect, useState, ReactNode } from "react"

type Theme = "dark" | "light" | "system"

interface ThemeContextValue {
  theme: Theme
  setTheme: (theme: Theme) => void
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined)

interface ThemeProviderProps {
  children: ReactNode
  defaultTheme?: Theme
  storageKey?: string
}

/**
 * ThemeProvider component
 * Manages theme state and applies theme classes to document root
 *
 * @param props - Component props including children and theme configuration
 * @returns Themed application wrapper
 */
export function ThemeProvider({
  children,
  defaultTheme = "system",
  storageKey = "taqat-theme",
}: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem(storageKey)
    return (stored as Theme) || defaultTheme
  })

  useEffect(() => {
    const root = window.document.documentElement

    const apply = (resolved: "light" | "dark") => {
      root.classList.remove("light", "dark")
      root.classList.add(resolved)
    }

    if (theme !== "system") {
      apply(theme)
      return
    }

    // In system mode the OS preference can change while the app is open, so
    // follow it rather than only reading it once.
    const query = window.matchMedia("(prefers-color-scheme: dark)")
    apply(query.matches ? "dark" : "light")

    const onChange = (event: MediaQueryListEvent) => apply(event.matches ? "dark" : "light")
    query.addEventListener("change", onChange)
    return () => query.removeEventListener("change", onChange)
  }, [theme])

  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      localStorage.setItem(storageKey, newTheme)
      setTheme(newTheme)
    },
  }

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

/**
 * Hook to access theme context
 * Must be used within ThemeProvider
 *
 * @returns Theme context value with current theme and setter
 */
export function useTheme() {
  const context = useContext(ThemeContext)

  if (context === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider")
  }

  return context
}
